import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import React from 'react'

import StreetViewModal from '@/components/Modal/Types/StreetView'
import { ModalVirtualTour } from '@/components/Modal/Types/VirtualTour'
import { ContextValue } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import { useModal } from '@/context/ModalContext'
import { ListingCate } from '@/page-commons/listingBySlug'
import getTranslatedTitle from '@/utils/get-translated-title'
import { handleListingDetailClick } from '@/utils/tracking/eventsHandlers'

import { DetailGalleryButton } from './DetailGalleryButton'
import Icon from './Icon'
import { buttonsWrapper } from './styles.css'

const ModalGalleryAndPlanimetry = dynamic(() =>
  import('@/components/Modal/Types/GalleryAndPlanimetry').then((module) => module.ModalGalleryAndPlanimetry)
)

type Props = {
  cities: ContextValue
  obfuscateExactLocation: boolean
  openMap: (e: React.SyntheticEvent<HTMLElement>) => void
  property: ListingCate
  showFloorPlan?: boolean
}

const Button: React.FC<Props> = (props) => {
  const device = useDevice()
  const { closeModal, openModal } = useModal()
  const { lang, t } = useTranslation()
  const hasVirtualTour = Boolean(props.property.virtualTours && props.property.virtualTours.url)

  const openStreetView = (e: React.SyntheticEvent<HTMLElement>) => {
    e.preventDefault()
    handleListingDetailClick('StreetView')
    openModal(<StreetViewModal cities={props.cities} closeModal={closeModal} property={props.property} />)
  }

  return (
    <div className={buttonsWrapper}>
      {hasVirtualTour && (
        <DetailGalleryButton
          Icon={() => <Icon kind="virtualTour" />}
          label={
            device === 'mobile'
              ? t('listing-detail:cta_show_virtual_tour_mobile')
              : t('listing-detail:cta_show_virtual_tour')
          }
          onClick={(event: React.SyntheticEvent<HTMLElement>) => {
            event.preventDefault()
            openModal(
              <ModalVirtualTour
                cities={props.cities}
                city={props.property.detail.city}
                closeModal={closeModal}
                device={device}
                language={lang}
                slug={props.property.slug}
                t={t}
                title={getTranslatedTitle(props.property.detail.titles, lang)}
                virtualTourUrl={props.property.virtualTours.url}
              />
            )

            handleListingDetailClick('VirtualTour')
          }}
        />
      )}
      {props.property.planimetries && props.property.planimetries.url && props.showFloorPlan && (
        <DetailGalleryButton
          Icon={() => <Icon kind="floorplan" />}
          label={t('listing-detail:planimetry')}
          onClick={(event: React.SyntheticEvent<HTMLElement>) => {
            event.preventDefault()
            openModal(
              <ModalGalleryAndPlanimetry
                cities={props.cities}
                citySlug={props.property.detail.city}
                closeModal={closeModal}
                images={props.property.photos}
                openType="planimetry"
                planimetryUrl={props.property.planimetries.url}
                startIndex={0}
                title={getTranslatedTitle(props.property.detail.titles, lang)}
              />
            )

            handleListingDetailClick('Planimetry')
          }}
        />
      )}
      {!props.obfuscateExactLocation && (
        <DetailGalleryButton
          data-testid="detail-gallery-button-map"
          Icon={() => <Icon kind="location" />}
          label={t('common:text-map')}
          onClick={props.openMap}
        />
      )}
      {!props.obfuscateExactLocation && (
        <DetailGalleryButton
          Icon={() => <Icon kind="streetView" />}
          label={t('listing-detail:street-view.cta')}
          onClick={openStreetView}
        />
      )}
    </div>
  )
}

export default Button
